import { Country } from '../const/country';
import { CompanySettingsDto } from '../dto/company-settings.dto';
import {
  CompanyFiscalType,
  FdmRequestHeader,
  BlackboxType
} from './company-fiscal-type.enum';
import { FeatureFlag } from '../dto/feature-flag';

const BLACKBOX_TYPE_MAP: Record<string, BlackboxType> = {
  CHECKBOX_EMULATOR: BlackboxType.CHECKBOX_EMULATOR,
  CHECKBOX: BlackboxType.CHECKBOX
};

function mapBlackboxTypeFromBackend(type: string): string {
  return BLACKBOX_TYPE_MAP[type] || type;
}

function mapBlackboxTypeToBackend(type: BlackboxType): string {
  switch (type) {
    case BlackboxType.CHECKBOX_EMULATOR:
      return 'CHECKBOX_EMULATOR';
    case BlackboxType.CHECKBOX:
      return 'CHECKBOX';
    default:
      return type;
  }
}

function parseFdmUrl(
  fdmUrl: string
): { useIpAddress: boolean; ipAddress: string } {
  if (!fdmUrl) {
    return { useIpAddress: false, ipAddress: '' };
  }
  const stripped = fdmUrl
    .replace(/^https?:\/\//, '')
    .replace(/\/graphql\/?$/, '');
  const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (!ipPattern.test(stripped)) {
    return { useIpAddress: false, ipAddress: '' };
  }
  return { useIpAddress: true, ipAddress: stripped };
}

function formatFdmUrl(companySettings: CompanySettings): string {
  const fdmType = companySettings.fdmType as BlackboxType;
  if (fdmType === BlackboxType.CHECKBOX) {
    if (companySettings.useIpAddress) {
      return companySettings.fdmUrl
        ? `http://${companySettings.fdmUrl}/graphql`
        : '';
    }
    return companySettings.fdmProductionNumber
      ? `http://${companySettings.fdmProductionNumber}.local/graphql`
      : '';
  }
  if (fdmType === BlackboxType.CHECKBOX_EMULATOR) {
    return `https://api.checkbox.be/checkboxemulator/graphql`;
  }
  return companySettings.fdmUrl;
}

export class CompanySettings {
  private static cantDeleteFdmTypes: RegExp = /^(quebec-srm|blackbox-restaurant)$/gi;
  private static cantDeleteCountries: Array<string> = [
    'Netherlands',
    'Malta',
    'Malaysia',
    'France',
    'Singapore'
  ];
  public activePmsIntegration: number;
  public addFloorReportToDayReport: boolean;
  public adminInCCForCustomerMail: boolean;
  public allowRoomChargeUpdates: boolean;
  public allowUnfinalizingNonRoomChargedReceipts: boolean;
  public annoncerEnabled: boolean;
  public assignProductSalesToReceiptOwner: boolean;
  public autoGenerateBlackBoxZReports: boolean;
  public autoLogin: boolean;
  public autoAcceptOrdersInKitchen: boolean;
  public bankAccountNumber: string;
  public blackBoxPinCode: string;
  public blackBoxSerialPortName: string;
  public bposNumber: string;
  public cposNumber: string;
  public cfdBackgroundUrl: string;
  public cfdEnabled: boolean;
  public cfdLogoUrl: string;
  public city: string;
  public closingReportsEnabled: boolean;
  public closingReportsSchedule: string;
  public closingTime: number;
  public combineBarAndKitchen: boolean;
  public condenseNegativeItemsWhenPrinting: boolean;
  public country: string;
  public csvDelimiter: string;
  public currency: string;
  public customerCardExpiryMonths: number;
  public customerSelectionPopup: boolean;
  public daysBeforePaymentExpiration: number;
  public description: string;
  public digitalMenuCardUrl: any;
  public disableBarCustomerPopup: boolean;
  public disableTabNumberOfCustomersPopup: boolean;
  public displayName: string;
  public email: string;
  public enableAdvancedReporting: boolean;
  public enforceCloud: boolean;
  public enforceLiteserver: boolean;
  public enforceStock: boolean;
  public fax: string;
  public featureFlagSuppressNewCondensingMode: FeatureFlag;
  public featureFlagMenuManagement: FeatureFlag;
  public featureFlagFlatCsvReports: FeatureFlag;
  public floorPlanInactivityInterval: number;
  public generateClosingTimeReport: boolean;
  public genericPmsApiPassword: string;
  public genericPmsApiUrl: string;
  public genericPmsApiUser: string;
  public gsm: string;
  public id: number;
  public imageLocation: string;
  public imageUrl: string;
  public info: string;
  public invoiceNumberTemplate: string;
  public invoiceTerms: string;
  public kdsEnabled: boolean;
  public kitchenAutoPrinting: boolean;
  public languageCode: string;
  public latitude: number;
  public listed: boolean;
  public locale: string;
  public longitude: number;
  public lsWeeksToStoreReceipt: number;
  public masterCompanyId: number;
  public maxReceiptImagesSaved: number;
  public menuEnabled: boolean;
  public multiLocationModifiersEnabled: boolean;
  public nafCode: string;
  public name: string;
  public notificationsEnabled: true;
  public notifyKitchen: boolean;
  public numberOfDecimals: number;
  public numberOfTaxDecimals: number;
  public onlySendRoomCharges: boolean;
  public openingTime: number;
  public parentId: any;
  public pmsIp: string;
  public pmsPort: number;
  public printLabourReportOnClockOut: boolean;
  public receiptsUneditableAfterClosingTime: boolean;
  public relativeImageLocation: string;
  public secondaryVatNumber: string;
  public server: any;
  public serverPort: number;
  public serviceChargeWithoutDiscount: boolean;
  public sharedEstablishmentCustomers: boolean;
  public shouldGoToTablesWhenNothingNeedsToBePrinted: boolean;
  public shouldWarnIfNonPrintedItems: boolean;
  public showAdditionsWithoutPrices: boolean;
  public showCashDrawerBalance: boolean;
  public showCashDrawerCashInOutDetails: boolean;
  public showCashDrawerDetailedReporting: boolean;
  public showCashDrawerReporting: boolean;
  public showCashDrawerReportingInFloorReport: boolean;
  public showCashDueInDayReport: boolean;
  public showCashDueInUserReport: boolean;
  public showDayReportCategoryDetails: boolean;
  public showDayReportDetails: boolean;
  public showDayReportFloorDetails: boolean;
  public showDayReportProductDetails: boolean;
  public showDayReportReceiptIds: boolean;
  public showDayReportTableDetails: boolean;
  public showDayReportTips: boolean;
  public showDiscountsByTypeAndUser: boolean;
  public showFreeItems: boolean;
  public showNumberOfPayments: boolean;
  public showPaymentsInReceiptDetails: boolean;
  public showPaymentsListInUserReport: boolean;
  public showProductReportAdditionValueDetails: boolean;
  public showProductReportAdditionValues: boolean;
  public showProductReportCostAndProfit: boolean;
  public showReportCreationTimeStamp: boolean;
  public showSalesAndTaxInfo: boolean;
  public showServiceChargeDetails: boolean;
  public showTotals: boolean;
  public showUserRevenueByCategoryType: boolean;
  public showUserSales: boolean;
  public showVoidDetails: boolean;
  public showWaiterSummaryInDayReport: boolean;
  public siretCode: string;
  public smtpFrom: string;
  public smtpHost: string;
  public smtpPassword: string;
  public smtpPort: number;
  public smtpUseTLS: boolean;
  public smtpUserName: string;
  public street: string;
  public streetNumber: number;
  public subTotalPricePrecision: number;
  public tabNavEnabled: boolean;
  public takeawayPopup: boolean;
  public taxEngineUrl: string;
  public taxEngineUrlDefault: string;
  public taxEngineUrlOverride: any;
  public taxProfile: any;
  public telephone: string;
  public timeZone: number;
  public timeZoneId: string;
  public tipPoolRate: number;
  public tipRefundRate: number;
  public totalPricePrecision: number;
  public type: CompanyFiscalType;
  public uploadDeviceLogs: boolean;
  public url: string;
  public useDetailedWeekReports: boolean;
  public useQueuingSystemForPms: boolean;
  public useReceiptCreationUserAsTipUser: boolean;
  public useReceiptOwnerAsPaymentOwner: boolean;
  public useSequentialIds: boolean;
  public useStockManagement: boolean;
  public useTaxClasses: boolean;
  public useUserClockTimes: boolean;
  public vat: string;
  public vatInclusive: boolean;
  public warnForNonPrintedItemsWhenReturning: boolean;
  public zReportsEnabled: boolean;
  public zip: string;
  public raw: any;
  public enableOpenVoidReason: boolean;

  public featureFlagMasUi: string;
  public fiscalName: string;
  public commercialName: string;
  public fdmUrl: string;
  public fdmType: string;
  public fdmProductionNumber: string;
  public fdmRequestHeaders: FdmRequestHeader[];
  public useIpAddress: boolean;

  public static fromDto(companyDto: CompanySettingsDto): CompanySettings {
    const company: CompanySettings = new CompanySettings();
    company.activePmsIntegration = companyDto.activePmsIntegration;
    company.addFloorReportToDayReport = companyDto.addFloorReportToDayReport;
    company.adminInCCForCustomerMail = companyDto.adminInCCForCustomerMail;
    company.allowRoomChargeUpdates = companyDto.allowRoomChargeUpdates;
    company.allowUnfinalizingNonRoomChargedReceipts =
      companyDto.allowUnfinalizingNonRoomChargedReceipts;
    company.annoncerEnabled = companyDto.annoncerEnabled;
    company.assignProductSalesToReceiptOwner =
      companyDto.assignProductSalesToReceiptOwner;
    company.autoGenerateBlackBoxZReports =
      companyDto.autoGenerateBlackBoxZReports;
    company.autoLogin = companyDto.autoLogin;
    company.autoAcceptOrdersInKitchen =
      companyDto.autoPreparePrintForOrdersEnabled;
    company.bankAccountNumber = companyDto.bankAccountNumber;
    company.blackBoxPinCode = companyDto.blackBoxPinCode;
    company.blackBoxSerialPortName = companyDto.blackBoxSerialPortName;
    const mac = companyDto.blackBoxServerMacAddress || '';
    company.bposNumber = mac.includes('BPOS') ? mac.replace(/^BPOS/, '') : '';
    company.cposNumber = mac.includes('CPOS') ? mac : '';
    company.cfdBackgroundUrl = companyDto.cfdBackgroundUrl;
    company.cfdEnabled = companyDto.cfdEnabled;
    company.cfdLogoUrl = companyDto.cfdLogoUrl || '';
    company.city = companyDto.city;
    company.closingReportsEnabled = companyDto.zReportsEnabled;
    company.closingReportsSchedule = companyDto.closingReportsSchedule;
    company.closingTime = companyDto.closingtime;
    company.combineBarAndKitchen = companyDto.combineBarAndKitchen;
    company.condenseNegativeItemsWhenPrinting =
      companyDto.condenseNegativeItemsWhenPrinting;
    company.country = companyDto.country;
    company.csvDelimiter = companyDto.csvDelimiter;
    company.currency = companyDto.currency;
    company.customerCardExpiryMonths =
      companyDto.defaultCustomerCardExpiryIntervalMonths;
    company.customerSelectionPopup = companyDto.customerSelectionPopup;
    company.daysBeforePaymentExpiration =
      companyDto.daysBeforePaymentExpiration;
    company.description = companyDto.info;
    company.digitalMenuCardUrl = companyDto.digitalMenuCardUrl;
    company.disableBarCustomerPopup = companyDto.disableBarCustomerPopup;
    company.disableTabNumberOfCustomersPopup =
      companyDto.disableTabNumberOfCustomersPopup;
    company.displayName = companyDto.displayName;
    company.email = companyDto.email;
    company.enableAdvancedReporting = companyDto.enableAdvancedReporting;
    company.enforceCloud = companyDto.enforceCloud;
    company.enforceLiteserver = companyDto.enforceLiteserver;
    company.enforceStock = companyDto.enforceStock;
    company.fax = companyDto.fax;
    company.fdmType = mapBlackboxTypeFromBackend(companyDto.fdmType);
    company.featureFlagSuppressNewCondensingMode = companyDto.featureFlagSuppressNewCondensingMode as FeatureFlag;
    company.featureFlagMenuManagement = companyDto.featureFlagMenuManagement as FeatureFlag;
    company.featureFlagFlatCsvReports = companyDto.featureFlagFlatCsvReports as FeatureFlag;
    company.featureFlagMasUi = companyDto.featureFlagMasUi as FeatureFlag;
    company.floorPlanInactivityInterval =
      companyDto.floorplanInactivityInterval;
    company.generateClosingTimeReport = companyDto.generateClosingTimeReport;
    company.genericPmsApiPassword = companyDto.genericPmsApiPassword;
    company.genericPmsApiUrl = companyDto.genericPmsApiUrl;
    company.genericPmsApiUser = companyDto.genericPmsApiUser;
    company.gsm = companyDto.gsm;
    company.id = companyDto.oid;
    company.imageLocation = companyDto.imageLocation;
    company.imageUrl = companyDto.imageUrl;
    company.invoiceNumberTemplate = companyDto.invoiceNumberTemplate;
    company.invoiceTerms = companyDto.invoiceTerms;
    company.kdsEnabled = companyDto.kdsEnabled;
    company.kitchenAutoPrinting = companyDto.kitchenAutoPrinting;
    company.languageCode = companyDto.languageCode || 'en-US';
    company.latitude = companyDto.latitude;
    company.listed = companyDto.listed;
    company.locale = companyDto.locale;
    company.longitude = companyDto.longitude;
    company.lsWeeksToStoreReceipt = companyDto.lsWeeksToStoreReceipt;
    company.masterCompanyId = companyDto.masterCompanyId;
    company.maxReceiptImagesSaved = companyDto.maxReceiptImagesSaved;
    company.menuEnabled = companyDto.menuEnabled;
    company.multiLocationModifiersEnabled =
      companyDto.multiLocationModifiersEnabled;
    company.nafCode = companyDto.nafCode;
    company.name = companyDto.name;
    company.notificationsEnabled = companyDto.notificationsEnabled;
    company.notifyKitchen = companyDto.notifyKitchen;
    company.numberOfDecimals = companyDto.numberOfDecimals;
    company.numberOfTaxDecimals = companyDto.numberOfTaxDecimals;
    company.onlySendRoomCharges = companyDto.onlySendRoomCharges;
    company.openingTime = companyDto.openingstime;
    company.parentId = companyDto.parentId;
    company.pmsIp = companyDto.pmsIp;
    company.pmsPort = companyDto.pmsPort;
    company.printLabourReportOnClockOut =
      companyDto.printLabourReportOnClockOut;
    company.raw = companyDto;
    company.receiptsUneditableAfterClosingTime =
      companyDto.receiptsUneditableAfterClosingTime;
    company.relativeImageLocation = companyDto.relativeImageLocation;
    company.secondaryVatNumber = companyDto.secondaryVatNumber;
    company.server = companyDto.server;
    company.serverPort = companyDto.serverPort;
    company.serviceChargeWithoutDiscount =
      companyDto.serviceChargeWithoutDiscount;
    company.sharedEstablishmentCustomers =
      companyDto.sharedEstablishmentCustomers;
    company.shouldGoToTablesWhenNothingNeedsToBePrinted =
      companyDto.shouldGoToTablesWhenNothingNeedsToBePrinted;
    company.shouldWarnIfNonPrintedItems =
      companyDto.shouldWarnIfNonPrintedItems;
    company.showAdditionsWithoutPrices = companyDto.showAdditionsWithoutPrices;
    company.showCashDrawerBalance = companyDto.showCashDrawerBalance;
    company.showCashDrawerCashInOutDetails =
      companyDto.showCashDrawerCashInOutDetails;
    company.showCashDrawerDetailedReporting =
      companyDto.showCashDrawerDetailedReporting;
    company.showCashDrawerReporting = companyDto.showCashDrawerReporting;
    company.showCashDrawerReportingInFloorReport =
      companyDto.showCashDrawerReportingInFloorReport;
    company.showCashDueInDayReport = companyDto.showCashDueInDayReport;
    company.showCashDueInUserReport = companyDto.showCashDueInUserReport;
    company.showDayReportCategoryDetails =
      companyDto.showDayReportCategoryDetails;
    company.showDayReportDetails = companyDto.showDayReportDetails;
    company.showDayReportFloorDetails = companyDto.showDayReportFloorDetails;
    company.showDayReportProductDetails =
      companyDto.showDayReportProductDetails;
    company.showDayReportReceiptIds = companyDto.showDayReportReceiptIds;
    company.showDayReportTableDetails = companyDto.showDayReportTableDetails;
    company.showDayReportTips = companyDto.showDayReportTips;
    company.showDiscountsByTypeAndUser = companyDto.showDiscountsByTypeAndUser;
    company.showFreeItems = companyDto.showFreeItems;
    company.showNumberOfPayments = companyDto.showNumberOfPayments;
    company.showPaymentsInReceiptDetails =
      companyDto.showPaymentsInReceiptDetails;
    company.showPaymentsListInUserReport =
      companyDto.showPaymentsListInUserReport;
    company.showProductReportAdditionValueDetails =
      companyDto.showProductReportAdditionValueDetails;
    company.showProductReportAdditionValues =
      companyDto.showProductReportAdditionValues;
    company.showProductReportCostAndProfit =
      companyDto.showProductReportCostAndProfit;
    company.showReportCreationTimeStamp =
      companyDto.showReportCreationTimeStamp;
    company.showSalesAndTaxInfo = companyDto.showSalesAndTaxInfo;
    company.showServiceChargeDetails = companyDto.showServiceChargeDetails;
    company.showTotals = companyDto.showTotals;
    company.showUserRevenueByCategoryType =
      companyDto.showUserRevenueByCategoryType;
    company.showUserSales = companyDto.showUserSales;
    company.showVoidDetails = companyDto.showVoidDetails;
    company.showWaiterSummaryInDayReport =
      companyDto.showWaiterSummaryInDayReport;
    company.siretCode = companyDto.siretCode;
    company.smtpFrom = companyDto.smtpFrom;
    company.smtpHost = companyDto.smtpHost;
    company.smtpPassword = companyDto.smtpPassword;
    company.smtpPort = companyDto.smtpPort;
    company.smtpUseTLS = companyDto.smtpUseTLS;
    company.smtpUserName = companyDto.smtpUserName;
    company.street = companyDto.street;
    company.streetNumber = companyDto.streetNumber;
    company.subTotalPricePrecision = parseFloat(
      companyDto.subTotalPricePrecision
    );
    company.tabNavEnabled = companyDto.tabNavEnabled;
    company.takeawayPopup = companyDto.takeawayPopup;
    company.taxEngineUrl = companyDto.taxEngineUrl;
    company.taxEngineUrlDefault = companyDto.taxEngineUrlDefault;
    company.taxEngineUrlOverride = companyDto.taxEngineUrlOverride;
    company.taxProfile = companyDto.taxProfile;
    company.telephone = companyDto.telephone;
    company.timeZone = companyDto.timezone;
    company.timeZoneId = companyDto.timeZoneID;
    company.tipPoolRate = companyDto.tipPoolRate;
    company.tipRefundRate = companyDto.tipRefundRate;
    company.totalPricePrecision = parseFloat(companyDto.totalPricePrecision);
    company.type = companyDto.type
      ? (companyDto.type as CompanyFiscalType)
      : CompanyFiscalType.NONE;
    company.uploadDeviceLogs = companyDto.uploadDeviceLogs;
    company.url = companyDto.url;
    company.useDetailedWeekReports = companyDto.useDetailedWeekReports;
    company.useQueuingSystemForPms = companyDto.useQueuingSystemForPms;
    company.useReceiptCreationUserAsTipUser =
      companyDto.useReceiptCreationUserAsTipUser;
    company.useReceiptOwnerAsPaymentOwner =
      companyDto.useReceiptOwnerAsPaymentOwner;
    company.useSequentialIds = companyDto.useSequentialIds;
    company.useStockManagement = companyDto.useStockManagement;
    company.useTaxClasses = companyDto.useTaxClasses;
    company.useUserClockTimes = companyDto.useUserClockTimes;
    company.vat = companyDto.vat;
    company.vatInclusive = companyDto.vatInclusive;
    company.warnForNonPrintedItemsWhenReturning =
      companyDto.warnForNonPrintedItemsWhenReturning;
    company.zip = companyDto.zip;
    company.enableOpenVoidReason = companyDto.enableOpenVoidReason;
    company.commercialName = companyDto.commercialName;
    company.fiscalName = companyDto.fiscalName;
    company.fdmType = mapBlackboxTypeFromBackend(companyDto.fdmType);
    const parsedFdmUrl = parseFdmUrl(companyDto.fdmUrl);
    company.fdmUrl = parsedFdmUrl.ipAddress;
    company.useIpAddress = parsedFdmUrl.useIpAddress;
    company.fdmProductionNumber = companyDto.fdmProductionNumber;
    company.fdmRequestHeaders = companyDto.fdmRequestHeaders;
    return company;
  }

  public static toDTO(companySettings: CompanySettings): CompanySettingsDto {
    const closingtime: number = companySettings.openingTime + 86400000;
    const isV1 = companySettings.type === CompanyFiscalType.BLACKBOX;
    const isV2 = companySettings.type === CompanyFiscalType.BLACKBOX2;
    const macAddress = isV1
      ? companySettings.bposNumber
        ? 'BPOS' + companySettings.bposNumber
        : null
      : isV2
      ? companySettings.cposNumber || null
      : null;

    const frontEndData: any = {
      activePmsIntegration: companySettings.activePmsIntegration,
      addFloorReportToDayReport: companySettings.addFloorReportToDayReport,
      adminInCCForCustomerMail: companySettings.adminInCCForCustomerMail,
      allowRoomChargeUpdates: companySettings.allowRoomChargeUpdates,
      allowUnfinalizingNonRoomChargedReceipts:
        companySettings.allowUnfinalizingNonRoomChargedReceipts,
      annoncerEnabled: companySettings.annoncerEnabled,
      assignProductSalesToReceiptOwner:
        companySettings.assignProductSalesToReceiptOwner,
      autoGenerateBlackBoxZReports:
        companySettings.autoGenerateBlackBoxZReports,
      autoLogin: companySettings.autoLogin,
      autoPreparePrintForOrdersEnabled:
        companySettings.autoAcceptOrdersInKitchen,
      bankAccountNumber: companySettings.bankAccountNumber,
      blackBoxPinCode: companySettings.blackBoxPinCode,
      blackBoxSerialPortName: companySettings.blackBoxSerialPortName,
      blackBoxServerMacAddress: macAddress,
      cfdBackgroundUrl: companySettings.cfdBackgroundUrl,
      cfdEnabled: companySettings.cfdEnabled,
      cfdLogoUrl: companySettings.cfdLogoUrl,
      city: companySettings.city,
      closingReportsEnabled: companySettings.closingReportsEnabled,
      closingReportsSchedule: companySettings.closingReportsSchedule,
      closingtime,
      combineBarAndKitchen: companySettings.combineBarAndKitchen,
      condenseNegativeItemsWhenPrinting:
        companySettings.condenseNegativeItemsWhenPrinting,
      country: companySettings.country,
      csvDelimiter: companySettings.csvDelimiter,
      currency: companySettings.currency,
      customerSelectionPopup: companySettings.customerSelectionPopup,
      daysBeforePaymentExpiration: companySettings.daysBeforePaymentExpiration,
      defaultCustomerCardExpiryIntervalMonths:
        companySettings.customerCardExpiryMonths,
      description: companySettings.description,
      digitalMenuCardUrl: companySettings.digitalMenuCardUrl,
      disableBarCustomerPopup: companySettings.disableBarCustomerPopup,
      disableTabNumberOfCustomersPopup:
        companySettings.disableTabNumberOfCustomersPopup,
      displayName: companySettings.displayName,
      email: companySettings.email,
      enableAdvancedReporting: companySettings.enableAdvancedReporting,
      enforceCloud: companySettings.enforceCloud,
      enforceLiteserver: companySettings.enforceLiteserver,
      enforceStock: companySettings.enforceStock,
      fax: companySettings.fax,
      featureFlagSuppressNewCondensingMode:
        companySettings.featureFlagSuppressNewCondensingMode,
      featureFlagMenuManagement: companySettings.featureFlagMenuManagement,
      featureFlagFlatCsvReports: companySettings.featureFlagFlatCsvReports,
      featureFlagMasUi: companySettings.featureFlagMasUi,
      floorplanInactivityInterval: companySettings.floorPlanInactivityInterval,
      generateClosingTimeReport: companySettings.generateClosingTimeReport,
      genericPmsApiPassword: companySettings.genericPmsApiPassword,
      genericPmsApiUrl: companySettings.genericPmsApiUrl,
      genericPmsApiUser: companySettings.genericPmsApiUser,
      gsm: companySettings.gsm,
      imageLocation: companySettings.imageLocation,
      imageUrl: companySettings.imageUrl,
      info: companySettings.description,
      invoiceNumberTemplate: companySettings.invoiceNumberTemplate,
      invoiceTerms: companySettings.invoiceTerms,
      kdsEnabled: companySettings.kdsEnabled,
      kitchenAutoPrinting: companySettings.kitchenAutoPrinting,
      languageCode: companySettings.languageCode,
      latitude: companySettings.latitude,
      listed: companySettings.listed,
      locale: companySettings.locale,
      longitude: companySettings.longitude,
      lsWeeksToStoreReceipt: companySettings.lsWeeksToStoreReceipt,
      masterCompanyId: companySettings.masterCompanyId,
      maxReceiptImagesSaved: companySettings.maxReceiptImagesSaved,
      menuEnabled: companySettings.menuEnabled,
      multiLocationModifiersEnabled:
        companySettings.multiLocationModifiersEnabled,
      nafCode: companySettings.nafCode,
      name: companySettings.name.trim(),
      notificationsEnabled: companySettings.notificationsEnabled,
      notifyKitchen: companySettings.notifyKitchen,
      numberOfDecimals: companySettings.numberOfDecimals,
      numberOfTaxDecimals: companySettings.numberOfTaxDecimals,
      oid: companySettings.id,
      onlySendRoomCharges: companySettings.onlySendRoomCharges,
      openingstime: companySettings.openingTime,
      parentId: companySettings.parentId,
      pmsIp: companySettings.pmsIp,
      pmsPort: companySettings.pmsPort,
      printLabourReportOnClockOut: companySettings.printLabourReportOnClockOut,
      receiptsUneditableAfterClosingTime:
        companySettings.receiptsUneditableAfterClosingTime,
      relativeImageLocation: companySettings.relativeImageLocation,
      secondaryVatNumber: companySettings.secondaryVatNumber,
      server: companySettings.server,
      serverPort: companySettings.serverPort,
      serviceChargeWithoutDiscount:
        companySettings.serviceChargeWithoutDiscount,
      sharedEstablishmentCustomers:
        companySettings.sharedEstablishmentCustomers,
      shouldGoToTablesWhenNothingNeedsToBePrinted:
        companySettings.shouldGoToTablesWhenNothingNeedsToBePrinted,
      shouldWarnIfNonPrintedItems: companySettings.shouldWarnIfNonPrintedItems,
      showAdditionsWithoutPrices: companySettings.showAdditionsWithoutPrices,
      showCashDrawerBalance: companySettings.showCashDrawerBalance,
      showCashDrawerCashInOutDetails:
        companySettings.showCashDrawerCashInOutDetails,
      showCashDrawerDetailedReporting:
        companySettings.showCashDrawerDetailedReporting,
      showCashDrawerReporting: companySettings.showCashDrawerReporting,
      showCashDrawerReportingInFloorReport:
        companySettings.showCashDrawerReportingInFloorReport,
      showCashDueInDayReport: companySettings.showCashDueInDayReport,
      showCashDueInUserReport: companySettings.showCashDueInUserReport,
      showDayReportCategoryDetails:
        companySettings.showDayReportCategoryDetails,
      showDayReportDetails: companySettings.showDayReportDetails,
      showDayReportFloorDetails: companySettings.showDayReportFloorDetails,
      showDayReportProductDetails: companySettings.showDayReportProductDetails,
      showDayReportReceiptIds: companySettings.showDayReportReceiptIds,
      showDayReportTableDetails: companySettings.showDayReportTableDetails,
      showDayReportTips: companySettings.showDayReportTips,
      showDiscountsByTypeAndUser: companySettings.showDiscountsByTypeAndUser,
      showFreeItems: companySettings.showFreeItems,
      showNumberOfPayments: companySettings.showNumberOfPayments,
      showPaymentsInReceiptDetails:
        companySettings.showPaymentsInReceiptDetails,
      showPaymentsListInUserReport:
        companySettings.showPaymentsListInUserReport,
      showProductReportAdditionValueDetails:
        companySettings.showProductReportAdditionValueDetails,
      showProductReportAdditionValues:
        companySettings.showProductReportAdditionValues,
      showProductReportCostAndProfit:
        companySettings.showProductReportCostAndProfit,
      showReportCreationTimeStamp: companySettings.showReportCreationTimeStamp,
      showSalesAndTaxInfo: companySettings.showSalesAndTaxInfo,
      showServiceChargeDetails: companySettings.showServiceChargeDetails,
      showTotals: companySettings.showTotals,
      showUserRevenueByCategoryType:
        companySettings.showUserRevenueByCategoryType,
      showUserSales: companySettings.showUserSales,
      showVoidDetails: companySettings.showVoidDetails,
      showWaiterSummaryInDayReport:
        companySettings.showWaiterSummaryInDayReport,
      siretCode: companySettings.siretCode,
      smtpFrom: companySettings.smtpFrom,
      smtpHost: companySettings.smtpHost,
      smtpPassword: companySettings.smtpPassword,
      smtpPort: companySettings.smtpPort,
      smtpUseTLS: companySettings.smtpUseTLS,
      smtpUserName: companySettings.smtpUserName,
      street: companySettings.street,
      streetNumber: companySettings.streetNumber,
      subTotalPricePrecision: companySettings.subTotalPricePrecision.toString(),
      tabNavEnabled: companySettings.tabNavEnabled,
      takeawayPopup: companySettings.takeawayPopup,
      taxEngineUrl: companySettings.taxEngineUrl,
      taxEngineUrlDefault: companySettings.taxEngineUrlDefault,
      taxEngineUrlOverride: companySettings.taxEngineUrlOverride,
      taxProfile: companySettings.taxProfile,
      telephone: companySettings.telephone,
      timeZoneID: companySettings.timeZoneId,
      timezone: companySettings.timeZone,
      tipPoolRate: companySettings.tipPoolRate,
      tipRefundRate: companySettings.tipRefundRate,
      totalPricePrecision: companySettings.totalPricePrecision.toString(),
      type: companySettings.type,
      uploadDeviceLogs: companySettings.uploadDeviceLogs,
      url: companySettings.url,
      useDetailedWeekReports: companySettings.useDetailedWeekReports,
      useQueuingSystemForPms: companySettings.useQueuingSystemForPms,
      useReceiptCreationUserAsTipUser:
        companySettings.useReceiptCreationUserAsTipUser,
      useReceiptOwnerAsPaymentOwner:
        companySettings.useReceiptOwnerAsPaymentOwner,
      useSequentialIds: companySettings.useSequentialIds,
      useStockManagement: companySettings.useStockManagement,
      useTaxClasses: companySettings.useTaxClasses,
      useUserClockTimes: companySettings.useUserClockTimes,
      vat: companySettings.vat,
      vatInclusive: companySettings.vatInclusive,
      warnForNonPrintedItemsWhenReturning:
        companySettings.warnForNonPrintedItemsWhenReturning,
      zReportsEnabled: companySettings.closingReportsEnabled,
      zip: companySettings.zip,
      enableOpenVoidReason: companySettings.enableOpenVoidReason,
      commercialName: companySettings.commercialName,
      fiscalName: companySettings.fiscalName,
      fdmType: mapBlackboxTypeToBackend(
        companySettings.fdmType as BlackboxType
      ),
      fdmUrl: formatFdmUrl(companySettings),
      fdmProductionNumber: companySettings.fdmProductionNumber,
      fdmRequestHeaders: companySettings.fdmRequestHeaders
    };
    return { ...companySettings.raw, ...frontEndData };
  }

  public static isVATRequiredForCountry(country: string): boolean {
    if (Boolean(country)) {
      return (
        [
          'belgium',
          'netherlands',
          'germany',
          'austria',
          'canada',
          'france',
          'monaco',
          'guadeloupe',
          'martinique',
          'reunion'
        ].indexOf(country.toLowerCase()) > -1
      );
    }
    return false;
  }

  public static canDeleteReceipts(companySettings: CompanySettings): boolean {
    return !(
      Country.isOneOfCountries(
        this.cantDeleteCountries,
        companySettings.country
      ) ||
      this.cantDeleteFdmTypes.test(companySettings.fdmType) ||
      companySettings.useSequentialIds
    );
  }

  public static isBlackBox(companySettings: CompanySettings): boolean {
    return (
      companySettings.type === CompanyFiscalType.BLACKBOX ||
      companySettings.type === CompanyFiscalType.BLACKBOX2
    );
  }

  public static isDutchFiscal(type: string): boolean {
    return type === CompanyFiscalType.DUTCH;
  }

  public static isFrenchFiscal(type: string): boolean {
    return type === CompanyFiscalType.FRENCH;
  }
}

export enum CLOSING_REPORTS_SCHEDULE {
  TWENTY_FOUR_HOUR_PERIOD = 'TWENTY_FOUR_HOUR_PERIOD',
  DAY_OPENING = 'DAY_OPENING'
}
